import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import { jsx as _jsx } from "react/jsx-runtime";
import _isEqual from "lodash/isEqual";
import { StatsigProvider as BaseStatsigProvider } from '@statsig/react-bindings';
import produce from 'immer';
import { useEffect, useMemo, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { UnexpectedError } from '../../error';
import { createContext } from '../../react';
export const [useExperimentContext, ExperimentContext] = createContext();
export const ExperimentProvider = ({
  children,
  client,
  initializeUser,
  fallback
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    if (!isInitialized) {
      const fetchingUserDataAndInitializeSync = function () {
        var _ref = _asyncToGenerator(function* () {
          const updatedUser = yield initializeUser(client.getContext().user);
          yield client.dataAdapter.prefetchData(updatedUser);
          client.updateUserSync(updatedUser);
          client.initializeSync();
        });

        return function fetchingUserDataAndInitializeSync() {
          return _ref.apply(this, arguments);
        };
      }();

      fetchingUserDataAndInitializeSync().then(() => {
        setIsInitialized(true);
      });
    }
  }, [client, initializeUser, isInitialized]);
  const [{
    loading: loadingUpdateUserId
  }, updateUserId] = useAsyncFn(function () {
    var _ref2 = _asyncToGenerator(function* (idKey, id) {
      if (!isInitialized) return;

      try {
        var _prevUser$customIDs, _prevUser$customIDs2;

        const prevUser = client.getContext().user;

        if (((_prevUser$customIDs = prevUser.customIDs) === null || _prevUser$customIDs === void 0 ? void 0 : _prevUser$customIDs[idKey]) && ((_prevUser$customIDs2 = prevUser.customIDs) === null || _prevUser$customIDs2 === void 0 ? void 0 : _prevUser$customIDs2[idKey]) === id) {
          return prevUser;
        }

        const updated = produce(prevUser, draft => {
          draft.customIDs ? draft.customIDs[idKey] = id : draft.customIDs = {
            [idKey]: id
          };
        });
        yield client.dataAdapter.prefetchData(updated);
        client.updateUserSync(updated);
        return updated;
      } catch (err) {
        throw new UnexpectedError('Fail to update user custom id', err);
      }
    });

    return function (_x, _x2) {
      return _ref2.apply(this, arguments);
    };
  }(), [client, isInitialized]);
  const [{
    loading: loadingUpdateUserProfile
  }, updateUserProfile] = useAsyncFn(function () {
    var _ref3 = _asyncToGenerator(function* (profileData) {
      if (!isInitialized) return;

      try {
        const prevUser = client.getContext().user;
        const updated = produce(prevUser, draft => {
          draft.custom = { ...(draft.custom || {}),
            ...profileData
          };
        });

        if (_isEqual(prevUser.custom, updated.custom)) {
          return prevUser;
        }

        yield client.dataAdapter.prefetchData(updated);
        client.updateUserSync(updated);
        return updated;
      } catch (err) {
        throw new UnexpectedError('Fail to update user profile', err);
      }
    });

    return function (_x3) {
      return _ref3.apply(this, arguments);
    };
  }(), [client, isInitialized]);
  const value = useMemo(() => ({
    updateUserId,
    updateUserProfile,
    loading: loadingUpdateUserId || loadingUpdateUserProfile
  }), [loadingUpdateUserId, loadingUpdateUserProfile, updateUserId, updateUserProfile]);

  if (!isInitialized) {
    return fallback !== null && fallback !== void 0 ? fallback : null;
  }

  return _jsx(BaseStatsigProvider, {
    client: client,
    children: _jsx(ExperimentContext.Provider, {
      value: value,
      children: children
    })
  });
};